import React, {FormEvent, useState} from "react";
import {Tab} from "@headlessui/react";
import TabButton from "../../../TabButton";
import {FormikContextType, useFormikContext} from "formik";
import {MultiSelectField, UrlField} from "../Field";
import Field from "../../Field";
import {useOperator} from "../../../../context/OperatorContext";
import {useQuery} from "react-query";
import ngage from "../../../../services/ngage";
import {Option, SingleCombobox} from "../Control";
import {LinkOptions} from "../../../../types/Link";
import IconName from "../../../../types/IconName";
import {toTitleCase} from "../../../../services/string";

// Types ----

export type LinkUrlFieldSetProps = {
  name: string
  offlineName?: string
  optionsName?: string
  options?: LinkOptions[]
  label?: string
}

type TabId = 'app' | 'web' | 'email' | 'phone'

type TabDefinition = {
  id: TabId
  name: string
}

// Helpers ----

const tabs: TabDefinition[] = [
  {id: 'app', name: 'App Content'},
  {id: 'web', name: 'Web Page'},
  {id: 'email', name: 'Email Address'},
  {id: 'phone', name: 'Phone Number'},
];

function tabPosition(index: number, count: number): number {
  switch (index) {
    case 0:
      return TabButton.positions.FIRST
    case (count - 1):
      return TabButton.positions.LAST
    default:
      return TabButton.positions.MIDDLE
  }
}

function tabIdFromUrl(url?: string): TabId {
  switch (true) {
    case url?.startsWith('ngage'):
      return 'app';
    case url?.startsWith('http'):
      return 'web';
    case url?.startsWith('tel'):
      return 'phone';
    case url?.startsWith('mailto'):
      return 'email';
    default:
      return 'app';
  }
}

function getFormValue(form: FormikContextType<any>, path: string) {
  const values = form.values as Record<string, any>;

  return (path.split('.').reduce((object, key) => object[key], values) as unknown) as string | undefined
}

function contentTypeFromUrl(url?: string) {
  if(!url || !url.startsWith('ngage')) return '';

  const path = url.split('://')[1] as string;

  return path.replace(/\d+/g, ':id');
}

function contentIdFromUrl(url?: string) {
  if(!url || !url.startsWith('ngage')) return null;

  const path = url.split('://')[1] as string;
  const id = path.split('/')[1];

  return id? Number(id) : null;
}

function urlFromContentType(operatorUuid: string, contentType: string, contentId?: number | string) {
  const path = contentType.includes(':id') ? contentType.replace(':id', contentId?.toString() ?? '') : contentType;

  const uuidSegments = operatorUuid.split('-');
  const namespace = `${uuidSegments[0]}${uuidSegments[4]}`;

  return `ngage${namespace}://${path}`;
}

async function getAchievementOptions(searchTerm: string): Promise<Option[]> {
  const {data: achievements} = await ngage.achievements.all();

  const filteredAchievements = searchTerm?
    achievements.filter(achievement => achievement.name.toLowerCase().includes(searchTerm.toLowerCase())) :
    achievements;

  return filteredAchievements.map(achievement => ({
    id: achievement.id,
    uuid: achievement.uuid,
    name: achievement.name,
  }));
}

async function getArticleOptions(searchTerm: string): Promise<Option[]> {
  const {data: articles} = await ngage.articles.filtered(searchTerm? {search: searchTerm} : {});

  return articles.map(article => ({
    id: article.id,
    uuid: article.uuid,
    name: article.title,
  }));
}

async function getCollectionOptions(searchTerm: string): Promise<Option[]> {
  const {data: collections} = await ngage.collections.all();

  const filteredCollections = searchTerm?
    collections.filter(collection => collection.name.toLowerCase().includes(searchTerm.toLowerCase())) :
    collections;

  return filteredCollections.map(collection => ({
    id: collection.id,
    uuid: collection.uuid,
    name: collection.name,
  }))
}

async function getEventOptions(searchTerm: string): Promise<Option[]> {
  const {data: events} = await ngage.events.filtered(searchTerm? {search: searchTerm} : {});

  return events.map(event => ({
    id: event.id,
    uuid: event.uuid,
    name: event.name,
    description: `${event.type.name} (${event.location.type === 'place' ? event.location.name : event.location.site.name})`,
  }));
}

async function getExhibitOptions(searchTerm: string): Promise<Option[]> {
  const searchFilter = searchTerm ? {search: searchTerm } : {};
  const {data: exhibits} = await ngage.exhibits.filtered({...searchFilter, published: true});

  return exhibits.map(exhibit => ({
    id: exhibit.id,
    uuid: exhibit.uuid,
    name: exhibit.name,
    description: `${exhibit.type.name} (${exhibit.zone.site.name})`,
  }));
}

async function getFacilityOptions(searchTerm: string): Promise<Option[]> {
  const searchFilter = searchTerm ? {search: searchTerm } : {};
  const {data: facilities} = await ngage.facilities.filtered({...searchFilter, published: true});

  return facilities.map(facility => ({
    id: facility.id,
    uuid: facility.uuid,
    name: facility.name,
    description: `${facility.type.name} (${facility.zone.site.name})`,
  }));
}

async function getQuizOptions(searchTerm: string): Promise<Option[]> {
  const {data: quizzes} = await ngage.quizzes.filtered(searchTerm? {search: searchTerm} : {});

  return quizzes.map(quiz => ({
    id: quiz.id,
    uuid: quiz.uuid,
    name: quiz.title,
  }));
}

async function getTrailOptions(searchTerm: string): Promise<Option[]> {
  const {data: trails} = await ngage.trails.filtered(searchTerm? {search: searchTerm} : {});

  return trails.map(trail => ({
    id: trail.id,
    uuid: trail.uuid,
    name: trail.name,
    description: trail.site?.name,
  }));
}

function getLinkOptionItems(options: LinkOptions[]): Option[] {
  return options
    .map(option => ({
      id: option,
      name: toTitleCase(option),
      icon: IconName.COG
    }));
}

// Component ----

export default function LinkUrlFieldSet({name, label, offlineName, optionsName = 'options', options = []}: LinkUrlFieldSetProps) {
  const {operator, termFor} = useOperator();
  const form = useFormikContext();

  // Check the form for the initial field value
  const formValue = getFormValue(form, name);

  // Keep track of the tab chosen
  const [selectedTabId, setSelectedTabId] = useState<TabId>(tabIdFromUrl(formValue));
  const selectedTabIndex = tabs.findIndex(tab => tab.id === selectedTabId);

  // Keep track of the selected content type and ID
  const [selectedContentType, setSelectedContentType] = useState<string>(contentTypeFromUrl(formValue));
  const [selectedContentId, setSelectedContentId] = useState<number|null>(contentIdFromUrl(formValue));

  // Handle tab selection
  function handleTabSelect(index: number) {
    void form.setFieldValue(name, '');
    if (offlineName) void form.setFieldValue(offlineName, '');
    void form.setFieldValue(optionsName, []);

    setSelectedTabId(tabs[index]?.id ?? 'app');
    setSearchTerm('');
    setSelectedContentType('');
    setSelectedContentId(null);
  }

  // Handle content type selection
  function handleSelectContentType({currentTarget}: FormEvent<HTMLSelectElement>) {
    if(!operator) return;

    void form.setFieldValue(name, urlFromContentType(operator.uuid, currentTarget.value));

    if(!!offlineName) void form.setFieldValue(offlineName, urlFromContentType(operator.uuid, currentTarget.value));

    // Validate the form field
    form.validateField(name).then(val => {
      void form.setTouched({[name]: true});
    });


    setSearchTerm('');
    setSelectedContentType(currentTarget.value);
  }

  // Handle content selection
  function handleSelectContentId(option: Option | null) {
    if(!operator || !option) return;

    void form.setFieldValue(name, urlFromContentType(operator.uuid, selectedContentType, Number(option.id)));

    if(!!offlineName) void form.setFieldValue(offlineName, urlFromContentType(operator.uuid, selectedContentType, String(option.uuid)));

    // Validate the form field
    form.validateField(name).then(val => {
      void form.setTouched({[name]: true});
    });

    setSearchTerm('');
    setSelectedContentId(Number(option.id));
  }

  // Handle offline URL change for external URLs
  function setOfflineUrl(url: string) {
    if (!offlineName) return;

    void form.setFieldValue(offlineName, url);
  }

  // Keep track of the current search term
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Find achievement options
  const {data: achievements, isLoading: isLoadingAchievements} = useQuery({
    queryKey: ['achievements', searchTerm],
    queryFn: () => getAchievementOptions(searchTerm),
    enabled: selectedContentType.startsWith('achievements/:id'),
  })

  // Find articles options
  const {data: articles, isLoading: isLoadingArticles} = useQuery({
    queryKey: ['articles', searchTerm],
    queryFn: () => getArticleOptions(searchTerm),
    enabled: selectedContentType.startsWith('articles/:id'),
  })

  // Find collection options
  const {data: collections, isLoading: isLoadingCollections} = useQuery({
    queryKey: ['collections', searchTerm],
    queryFn: () => getCollectionOptions(searchTerm),
    enabled: selectedContentType.startsWith('collections/:id'),
  })

  // Find event options
  const {data: events, isLoading: isLoadingEvents} = useQuery({
    queryKey: ['events', searchTerm],
    queryFn: () => getEventOptions(searchTerm),
    enabled: selectedContentType.startsWith('events/:id'),
  })

  // Find exhibit options
  const {data: exhibits, isLoading: isLoadingExhibits} = useQuery({
    queryKey: ['exhibits', searchTerm],
    queryFn: () => getExhibitOptions(searchTerm),
    enabled: selectedContentType.startsWith('exhibits/:id'),
  })

  // Find facility options
  const {data: facilities, isLoading: isLoadingFacilities} = useQuery({
    queryKey: ['facilities', searchTerm],
    queryFn: () => getFacilityOptions(searchTerm),
    enabled: selectedContentType.startsWith('facilities/:id'),
  })

  // Find quiz options
  const {data: quizzes, isLoading: isLoadingQuizzes} = useQuery({
    queryKey: ['quizzes', searchTerm],
    queryFn: () => getQuizOptions(searchTerm),
    enabled: selectedContentType.startsWith('quizzes/:id'),
  })

  // Find trail options
  const {data: trails, isLoading: isLoadingTrails} = useQuery({
    queryKey: ['trails', searchTerm],
    queryFn: () => getTrailOptions(searchTerm),
    enabled: selectedContentType.startsWith('trails/:id'),
  })

  const optionItems = getLinkOptionItems(options);

  // Render component
  return (
    <fieldset>
      {!!label &&
        <legend className="block text-sm font-medium leading-6 text-gray-900">
          {label}
        </legend>
      }

      {/* URL Type */}
      <div className="mb-2 mt-2">
        <Tab.Group selectedIndex={selectedTabIndex} onChange={handleTabSelect}>
          <Tab.List>
            {tabs.map((tab, index, tabList) => (
              <TabButton position={tabPosition(index, tabList.length)} key={index}>
                {tab.name}
              </TabButton>
            ))}
          </Tab.List>
        </Tab.Group>
      </div>

      {/* URL Body */}
      <div>
        {selectedTabId === 'app' &&
          <div className="sm:grid sm:grid-cols-3 sm:gap-2">
            <div className="sm:col-span-1">
              <Field
                as='select'
                id={name}
                name={name}
                value={selectedContentType}
                onChange={handleSelectContentType}
              >
                <option value='' disabled>Select content type...</option>

                <optgroup label='Account'>
                  <option value='user'>User Account Details</option>
                </optgroup>

                <optgroup label={termFor('Achievements')}>
                  <option value='achievements'>{`All ${termFor('Achievements')}`}</option>
                  <option value='achievements/:id'>{`${termFor('Achievement')} Information`}</option>
                </optgroup>

                <optgroup label='Activities'>
                  <option value='activities'>All Activities</option>
                </optgroup>

                <optgroup label={termFor('Collections')}>
                  <option value='collections'>{`All ${termFor('Collections')}`}</option>
                  <option value='collections/:id'>{`${termFor('Collection')} Information`}</option>
                </optgroup>

                <optgroup label={termFor('Events')}>
                  <option value='events'>{`All ${termFor('Events')}`}</option>
                  <option value='events/:id'>{`${termFor('Event')} Information`}</option>
                </optgroup>

                <optgroup label={termFor('Exhibits')}>
                  <option value='exhibits'>{`All ${termFor('Exhibits')}`}</option>
                  <option value='exhibits/:id'>{`${termFor('Exhibit')} Information`}</option>
                  <option value='exhibits/:id/notes'>{`${termFor('Exhibit')} Notes`}</option>
                  <option value='exhibits/:id/quizzes'>{`${termFor('Exhibit')} Quizzes`}</option>
                  <option value='exhibits/:id/sponsorships'>{`${termFor('Exhibit')} ${termFor('Sponsorships')}`}</option>
                </optgroup>

                <optgroup label={termFor('Facilities')}>
                  <option value='facilities'>{`All ${termFor('Facilities')}`}</option>
                  <option value='facilities/:id'>{`${termFor('Facility')} Information`}</option>
                </optgroup>

                <optgroup label='Frames'>
                  <option value='frames'>Capture Your Day</option>
                </optgroup>

                <optgroup label='News'>
                  <option value='articles'>All News</option>
                  <option value='articles/:id'>Article</option>
                </optgroup>

                <optgroup label='Overview'>
                  <option value='overview'>Overview</option>
                </optgroup>

                <optgroup label='Quizzes'>
                  <option value='quizzes/:id'>Specific Quiz</option>
                </optgroup>

                <optgroup label={termFor('Sites')}>
                  <option value='sites'>{`${termFor('Site')} Selector`}</option>
                </optgroup>

                <optgroup label={termFor('Trails')}>
                  <option value='trails'>{`All ${termFor('Trails')}`}</option>
                  <option value='trails/:id'>{`${termFor('Trail')} Information`}</option>
                </optgroup>

                <optgroup label={termFor('Visits')}>
                  <option value='visits'>{`${termFor('Visit')} Planner`}</option>
                </optgroup>

                <optgroup label="Wallet">
                  <option value='tickets'>{termFor('Tickets')}</option>
                  <option value='memberships'>{termFor('Memberships')}</option>
                  <option value='vouchers'>{termFor('Vouchers')}</option>
                </optgroup>
              </Field>
            </div>

            {selectedContentType.includes(':id') &&
              <div className="sm:col-span-2">
                {selectedContentType.startsWith('achievements/:id') &&
                  <SingleCombobox
                    isLoading={isLoadingAchievements}
                    options={achievements ?? []}
                    selected={achievements?.find(option => option.id === selectedContentId)}
                    onSearch={term => setSearchTerm(term ?? '')}
                    onChange={handleSelectContentId}
                  />
                }

                {selectedContentType.startsWith('collections/:id') &&
                  <SingleCombobox
                    isLoading={isLoadingCollections}
                    options={collections ?? []}
                    selected={collections?.find(option => option.id === selectedContentId)}
                    onSearch={term => setSearchTerm(term ?? '')}
                    onChange={handleSelectContentId}
                  />
                }

                {selectedContentType.startsWith('events/:id') &&
                  <SingleCombobox
                    isLoading={isLoadingEvents}
                    options={events ?? []}
                    selected={events?.find(option => option.id === selectedContentId)}
                    onSearch={term => setSearchTerm(term ?? '')}
                    onChange={handleSelectContentId}
                  />
                }

                {selectedContentType.startsWith('exhibits/:id') &&
                  <SingleCombobox
                    isLoading={isLoadingExhibits}
                    options={exhibits ?? []}
                    selected={exhibits?.find(option => option.id === selectedContentId)}
                    onSearch={term => setSearchTerm(term ?? '')}
                    onChange={handleSelectContentId}
                  />
                }

                {selectedContentType.startsWith('facilities/:id') &&
                  <SingleCombobox
                    isLoading={isLoadingFacilities}
                    options={facilities ?? []}
                    selected={facilities?.find(option => option.id === selectedContentId)}
                    onSearch={term => setSearchTerm(term ?? '')}
                    onChange={handleSelectContentId}
                  />
                }

                {selectedContentType.startsWith('articles/:id') &&
                  <SingleCombobox
                    isLoading={isLoadingArticles}
                    options={articles ?? []}
                    selected={articles?.find(option => option.id === selectedContentId)}
                    onSearch={term => setSearchTerm(term ?? '')}
                    onChange={handleSelectContentId}
                  />
                }

                {selectedContentType.startsWith('quizzes/:id') &&
                  <SingleCombobox
                    isLoading={isLoadingQuizzes}
                    options={quizzes ?? []}
                    selected={quizzes?.find(option => option.id === selectedContentId)}
                    onSearch={term => setSearchTerm(term ?? '')}
                    onChange={handleSelectContentId}
                  />
                }

                {selectedContentType.startsWith('trails/:id') &&
                  <SingleCombobox
                    isLoading={isLoadingTrails}
                    options={trails ?? []}
                    selected={trails?.find(option => option.id === selectedContentId)}
                    onSearch={term => setSearchTerm(term ?? '')}
                    onChange={handleSelectContentId}
                  />
                }
              </div>
            }
          </div>
        }

        {selectedTabId === 'web' &&
          <>
            <UrlField name={name} schemes={["https"]} onChangeUrl={setOfflineUrl} />

            {!!options?.length &&
              <div className="mt-3">
                <MultiSelectField
                  name={optionsName}
                  label="Options"
                  options={optionItems}
                />
              </div>
            }
          </>
        }

        {selectedTabId === 'email' &&
          <UrlField name={name} schemes={["mailto"]} onChangeUrl={setOfflineUrl} />
        }

        {selectedTabId === 'phone' &&
          <UrlField name={name} schemes={["tel"]} onChangeUrl={setOfflineUrl} />
        }
      </div>

    </fieldset>
  );
}